import { Box, chakra } from '@cardboard-ui/react';
import { Trans } from '@lingui/macro';
import { MentionAtomNodeAttributes } from '@remirror/extension-mention-atom';
import { useActive } from '@remirror/react';
import { FloatingWrapper } from '@remirror/react-components';
import { useMentionAtom } from '@remirror/react-hooks';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { cx } from 'remirror';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { useSession } from '../../../utils/sessionProvider';
import { UserSuggester_allMembers_Query } from './__generated__/UserSuggester_allMembers_Query.graphql';

const membersQuery = graphql`
  query UserSuggester_allMembers_Query {
    sessionInfo {
      spaces {
        id
        memberships {
          nodes {
            member {
              id
              name
              email
            }
          }
        }
      }
    }
  }
`;

interface UserSuggesterProps {
  spaceId?: string;
  participantIds?: string[];
  setIsUserSuggesterOpen?: (isOpen: boolean) => void;
}

const UserSuggester: React.FC<UserSuggesterProps> = (props) => (
  <Suspense
    fallback={null}
    children={<UnsuspendedUserSuggester {...props} />}
  />
);

export default UserSuggester;

function UnsuspendedUserSuggester({
  spaceId = '',
  participantIds = [],
  setIsUserSuggesterOpen,
}: UserSuggesterProps) {
  const { useLazyLoadQuery } = useSafeRelay();

  const [suggestedUsers, setSuggestedUsers] = useState<
    MentionAtomNodeAttributes[]
  >([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  const active = useActive();
  const { state, getMenuProps, getItemProps, indexIsHovered, indexIsSelected } =
    useMentionAtom({
      items: suggestedUsers,
      submitKeys: ['Tab', 'Enter'],
      dismissKeys: ['Escape'],
    });
  const { member: currentMember } = useSession();
  const currentMemberId = currentMember?.id;

  const {
    sessionInfo: { spaces },
  } = useLazyLoadQuery<UserSuggester_allMembers_Query>(membersQuery, {});

  const members = useMemo(
    () =>
      Object.values(
        spaces
          .flatMap(({ memberships, id }) =>
            memberships.nodes.map(({ member }) => ({ ...member, spaceId: id })),
          )
          .map((member) => member)
          .reduce((members, member) => {
            if (!members[member.id]) {
              members[member.id] = {
                id: member.id,
                email: member.email,
                name: member.name,
                spaceIds: [member.spaceId],
              };
            } else {
              members[member.id].spaceIds.push(member.spaceId);
            }
            return members;
          }, {} as Record<string, { id: string; email: string; name: string; spaceIds: string[] }>),
      ),
    [spaces],
  );

  useEffect(() => {
    const searchTerm = state?.query.full.toLowerCase();
    const filteredUsers = members
      .filter(({ spaceIds }) => !!spaceId || spaceIds.includes(spaceId))
      .filter(
        ({ id }) => participantIds.length === 0 || participantIds.includes(id),
      )
      .filter(
        (member) =>
          !searchTerm || member.name.toLowerCase().includes(searchTerm),
      )
      .sort()
      .slice(0, 10);

    setSuggestedUsers(
      filteredUsers.map((member) => {
        return {
          id: member.id,
          label: member.name,
        };
      }),
    );
  }, [state, participantIds.join(','), spaceId]);

  useEffect(() => {
    setEnabled(!!state && !active.todo?.());
  }, [state]);

  useEffect(() => {
    if (setIsUserSuggesterOpen) {
      setIsUserSuggesterOpen(enabled);
    }
  }, [enabled]);

  // Adding a performance hack here.
  // Having many FloatingWrapper on a page (like with comments) causes a flurry
  // of intersectionObservers to fire, even when enabled is false. This also causes
  // rerenders to occur, completely tanking the performance of the page.
  //
  // This may be fixed in later version of Remirror, or when fully
  // enabling react 18 render mode.
  if (!enabled) {
    return <></>;
  }

  return (
    <FloatingWrapper
      positioner="cursor"
      enabled={enabled}
      placement="bottom-start"
      renderOutsideEditor
    >
      <chakra.div
        {...getMenuProps()}
        className="suggestions"
        backgroundColor="var(--chakra-colors-gray-100)"
        _dark={{
          backgroundColor: 'var(--chakra-colors-gray-700)',
        }}
      >
        {enabled &&
          suggestedUsers.map((user, index) => {
            const isHighlighted = indexIsSelected(index);
            const isHovered = indexIsHovered(index);

            return (
              <chakra.div
                key={user.id}
                className={cx(
                  'suggestion',
                  isHighlighted && 'highlighted',
                  isHovered && 'hovered',
                )}
                {...getItemProps({
                  item: user,
                  index,
                })}
                cursor="pointer"
              >
                {user.label}
                {user.id === currentMemberId && (
                  <Box display="inline-block" ml={2}>
                    (
                    <Trans context="Indication that a member in a list is the current user.">
                      you
                    </Trans>
                    )
                  </Box>
                )}
              </chakra.div>
            );
          })}
      </chakra.div>
    </FloatingWrapper>
  );
}
